import { analectById } from "../engine";
import type { Challenge } from "../engine";
import { LEARNING_MANIFEST_VERSION, type LearningUnitCompletion } from "./learningEvidence";
import { waitForIdentity } from "./identity";

export interface LearningOperation {
  operationId: string;
  action: string;
  occurredAt: string;
  parentOperationId: string;
  [key: string]: unknown;
}
export interface CaptureState { status: string; code?: string }
export interface CaptureService {
  scope: string | null;
  id(): string;
  build(action: string, content: unknown, context: Record<string, unknown>, options?: Record<string, unknown>): LearningOperation;
  record(operation: LearningOperation): Promise<unknown>;
  prepare(): Promise<unknown>;
  resume(): Promise<unknown>;
  retry(): Promise<unknown>;
  invalidate(): void;
  onState(callback: (state: CaptureState) => void): () => void;
}
export interface AnswerCapture { operations: LearningOperation[]; scope: string | null; key: string; feedbackTimeCaptured: boolean }
const previousAnswers = new Map<string, string>();
function previousAnswer(key: string): string {
  try { return localStorage.getItem(`ly:record-parent:${key}`) || previousAnswers.get(key) || ""; }
  catch { return previousAnswers.get(key) || ""; }
}
let session: { scope: string | null; key: string } | null = null;

export function captureService(): CaptureService {
  const service = window.BdfzLearningRecords;
  if (!service) throw new Error("LEARNING_RECORDER_NOT_READY");
  return service;
}
// The SDK loads asynchronously from my.bdfz.net; wait long enough for a slow load instead of giving up after
// the 2.5 s default, after which the first answers would be captured without an owner.
export const IDENTITY_WAIT_MS = 15000;
export async function prepareLearningRecords(): Promise<void> {
  await waitForIdentity(IDENTITY_WAIT_MS);
  await captureService().prepare();
}
// Build synchronously at the actual answer click. Never adopt a later login or
// reconstruct missing timestamps/content from the legacy completion container.
export function buildAnswerCapture(challenge: Challenge, optionId: string, completion: LearningUnitCompletion,
  service = captureService(), feedbackAt?: string): AnswerCapture {
  const scope = service.scope;
  if (!session || session.scope !== scope) session = { scope, key: `ly:session:${service.id()}` };
  const quote = analectById.get(completion.quoteId);
  const context = { captureScope: scope, sessionKey: session.key, resourceKey: completion.resourceKey,
    resourceVersion: LEARNING_MANIFEST_VERSION, chapterId: completion.quoteId,
    chapterTitle: quote?.title || "", interactionType: "source_question_answer",
    sourceContext: { gate: completion.gate, bankId: challenge.bankId || null, questionSource: challenge.source,
      questionReview: challenge.questionReview ?? null,
      attemptNumber: completion.attemptNumber, feedbackOrigin: "source_explanation", model: null } };
  const key = `${scope || "unconfirmed"}:${completion.resourceKey}`;
  const snapshot = JSON.parse(JSON.stringify({ quote: quote || null, challenge }));
  const answer = service.build("answer.submit", { ...snapshot, selectedOptionId: optionId,
    selectedText: challenge.options.find(option => option.id === optionId)?.text || "" }, context,
    { operationId: `ly:${completion.eventNonce}:answer`, occurredAt: new Date(completion.submittedAt).toISOString(),
      parentOperationId: previousAnswer(key), actor: "student", status: "succeeded",
      assessment: { scoringEligibility: "record_only" } });
  const feedback = service.build("source.feedback", { evidence: challenge.evidence,
    optionWhy: challenge.optionWhy || {}, purpose: challenge.purpose || "", correctOptionId: challenge.answerId,
    correct: completion.correct, source: challenge.source, model: null }, context,
    { operationId: `ly:${completion.eventNonce}:feedback`, occurredAt: feedbackAt, parentOperationId: answer.operationId,
      actor: "system", status: "succeeded", contentOrigin: "source_explanation",
      assessment: { scoringEligibility: "record_only", observedCorrectness: completion.correct,
        observedScorePercent: completion.correct ? 100 : 0, authoritativeGrade: false } });
  return { operations: [answer, feedback], scope, key, feedbackTimeCaptured: feedbackAt !== undefined };
}
export async function saveAnswerCapture(capture: AnswerCapture, service = captureService()): Promise<void> {
  // The caller keeps these same objects/IDs on a storage retry. Both rows become
  // durable before the existing UI/completion path continues.
  await service.record(capture.operations[0]);
  if (!capture.feedbackTimeCaptured) {
    capture.operations[1].occurredAt = new Date().toISOString();
    capture.feedbackTimeCaptured = true;
  }
  await service.record(capture.operations[1]);
  previousAnswers.set(capture.key, capture.operations[0].operationId);
  try { localStorage.setItem(`ly:record-parent:${capture.key}`, capture.operations[0].operationId); }
  catch { /* Journal rows stay durable; unavailable old ancestry is never invented. */ }
}
export function captureMessage(state: CaptureState | null): string {
  if (!state) return "";
  if (state.status === "unattributed") return "完整作答已保留在本機；登入前內容不會自動歸屬";
  if (state.status === "saved") return "完整作答與解析已同步";
  if (state.status === "saved_locally" || state.status === "pending" || state.status === "offline") return "完整作答已保留在本機，等待同步";
  if (state.code === "LEARNING_LOGIN_REQUIRED") return "登入後可同步完整作答與解析";
  if (state.status === "storage_error") return "完整作答尚未保存，請保留此頁並重試";
  if (state.status === "needs_attention") return "完整作答暫未同步，請保留此頁並重試";
  return "";
}
